import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ExclamationSpeechIcon } from '@domgen/base-design-system-2.0/icons/ExclamationSpeech';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StoreEvent } from '../enums/storeEvent';
import { useIsTenantFeatureEnabled } from '../hooks';
import { useOTEL } from '../hooks/useOTEL';
import { capturePostHogEvent } from '../services/posthog/capture';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useConfirmation } from '../store/selectors/useConfirmation';
import { useGaEventData } from '../store/selectors/useGaEventData';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { store } from '../store/store';
import { getSystemCode } from '../utils/getSystemCode';
import { enhanceSpan } from '../utils/otel/enhanceTrace';
import { formApplianceName } from '../utils/store/formApplianceName';
import { formCategoryName } from '../utils/store/formCategoryName';

export function ErrorPage() {
  const { t } = useTranslation('common', {
    keyPrefix: 'error',
  });
  const isPhoneNumberDisplayed = useIsTenantFeatureEnabled('errorPageCallUs');
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const formData = useApplianceFormData();
  const confirmation = useConfirmation();
  const gaEventData = useGaEventData();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const phoneNumber = t('phoneNumber');
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();
  const { addEvent } = useOTEL();

  useEffect(() => {
    addEvent('ErrorPageViewed');
    enhanceSpan(
      'olr',
      {
        systemCode,
        sessionId: session?.id ?? 'unknown',
      },
      true,
    );

    gaPageView({
      pagename: 'generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided', // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });
  }, []);

  const handlePhoneNumber = () => {
    addEvent('ErrorPagePhoneNumberClicked');

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Generic Error page',
      eventLabel: 'Phone Number clicked',
      pagename: 'generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided', // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });

    capturePostHogEvent(posthog, 'error-phone-clicked', store.get());
  };

  const handleTryAgain = () => {
    addEvent('ErrorPageTryAgainClicked');

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Generic Error page',
      eventLabel: 'Try Again button clicked',
      pagename: 'generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided',
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });

    capturePostHogEvent(posthog, 'error-try-again-clicked', store.get());
    store.send({ type: StoreEvent.INITIALISE });
    navigate('/', { replace: true });
  };

  return (
    <div className="grid place-items-center gap-4 mx-auto w-11/12 md:w-9/12 py-10 ">
      <ExclamationSpeechIcon />
      <h1 className="text-3xl font-semibold text-center">{t('heading')}</h1>
      <p className="text-center">{t('description')}</p>
      <p className="text-center">
        <strong>{t('contactPrompt')}</strong>
      </p>
      {isPhoneNumberDisplayed && (
        <TextLink onClick={handlePhoneNumber} href={`tel:${phoneNumber.replace(/[^\d+]/g, '')}`}>
          {phoneNumber}
        </TextLink>
      )}
      <div className="w-full md:max-w-[360px]">
        <Button variant="primary" onClick={handleTryAgain}>
          {t('tryAgain')}
        </Button>
      </div>
    </div>
  );
}
